import React, { useState, useEffect, useCallback } from 'react';
import { get, post } from 'aws-amplify/api';

// 運営者用：全テナント横断のセキュリティレポート（不審なログイン・権限の変更・設定の弱点）
const PERIODS = [
  { days: 1, label: '24時間' },
  { days: 7, label: '7日間' },
  { days: 30, label: '30日間' },
  { days: 90, label: '90日間' },
];

const ACTION_LABELS = {
  user_created: 'ユーザー追加', user_deleted: 'ユーザー削除', user_updated: 'ユーザー情報変更',
  user_frozen: '凍結', user_unfrozen: '凍結解除', password_reset: 'パスワードリセット',
  mfa_reset: 'MFAリセット', mfa_registered: '二段階認証を登録', plan_changed: 'プラン変更', max_users_updated: 'ユーザー上限変更',
};

const formatDateTime = (v) => (v ? new Date(v).toLocaleString('ja-JP') : '—');

const extractErrorMessage = async (e, fallback) => {
  try { const b = await e.response.body.json(); return b.error || fallback; } catch { return fallback; }
};

// ユーザー名（管理者には印）と会社名
function Who({ row, onOpenTenant }) {
  return (
    <>
      <div className="opc-user-name">
        {row.display_name || row.username || row.user_id}
        {row.role === 'admin' && <span className="opc-badge opc-badge-admin">管理者</span>}
      </div>
      <button className="opc-link opc-sec-tenant" onClick={() => onOpenTenant(row.tenant_id)}>
        {row.company_name || row.tenant_id}
      </button>
    </>
  );
}

// 見出し＋件数＋表（0件なら「問題なし」）
function Block({ title, hint, rows, level = 'warn', columns, renderRow, emptyText = '該当なし' }) {
  const [open, setOpen] = useState(rows.length > 0);
  useEffect(() => { setOpen(rows.length > 0); }, [rows.length]);
  return (
    <section className={`opc-sec-block ${rows.length > 0 ? `opc-sec-${level}` : 'opc-sec-ok'}`}>
      <button className="opc-sec-head" onClick={() => setOpen((v) => !v)}>
        <span className="opc-sec-icon">{rows.length > 0 ? (level === 'info' ? 'ℹ️' : '⚠️') : '✅'}</span>
        <span className="opc-sec-title">{title}</span>
        <span className="opc-sec-count">{rows.length}件</span>
        <span className="opc-sec-toggle">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="opc-sec-body">
          {hint && <p className="opc-sec-hint">{hint}</p>}
          {rows.length === 0 ? (
            <div className="opc-empty">{emptyText}</div>
          ) : (
            <div className="opc-table-wrap">
              <table className="opc-table">
                <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{rows.map(renderRow)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function OperatorSecurity({ onOpenTenant }) {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');

  const fetchReport = useCallback(async (d) => {
    setLoading(true);
    setError('');
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tenants/operator/security', options: { queryParams: { days: String(d) } } });
      const { body } = await op.response;
      setReport(await body.json());
    } catch (e) {
      const msg = await extractErrorMessage(e, 'セキュリティレポートの取得に失敗しました');
      setError(msg === 'operator_mfa_required'
        ? '運営管理画面を使うには、MFA（多要素認証）の設定が必要です。マイプロフィールから設定してください。'
        : msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(days); }, [days, fetchReport]);

  const handleSendNow = async () => {
    if (!window.confirm('直近7日間のセキュリティレポートを、運営者のメールアドレスに今すぐ送信します。')) return;
    setSending(true);
    setNotice('');
    try {
      const op = post({ apiName: 'worktimerApi', path: '/tenants/operator/security/send-report' });
      await op.response;
      setNotice('レポートをメールで送信しました');
    } catch (e) {
      setNotice(await extractErrorMessage(e, 'レポートの送信に失敗しました'));
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="opc-message opc-message-error">{error}</div>;

  const s = report?.summary;

  return (
    <div className="opc-sec">
      <div className="opc-sec-toolbar">
        <div className="opc-sec-periods">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              className={`opc-sec-period ${days === p.days ? 'active' : ''}`}
              onClick={() => setDays(p.days)}
              disabled={loading}
            >
              直近{p.label}
            </button>
          ))}
        </div>
        <div className="opc-sec-actions">
          <button className="opc-btn-small" onClick={() => fetchReport(days)} disabled={loading}>🔄 再読み込み</button>
          <button className="opc-btn-small" onClick={handleSendNow} disabled={sending}>📧 レポートを今すぐメール送信</button>
        </div>
      </div>
      <p className="opc-sec-note">
        毎週月曜 9:00 に、直近7日間のレポートが運営者のメールアドレスへ自動で届きます。
        ログイン履歴はログインに成功したときの記録です。パスワードを間違えた試行は含まれません。
      </p>
      {notice && <div className="opc-message opc-message-success">{notice}<button className="opc-message-close" onClick={() => setNotice('')} aria-label="閉じる">✕</button></div>}

      {loading || !report ? (
        <div className="opc-empty">読み込み中…</div>
      ) : (
        <>
          <div className={`opc-sec-banner ${report.alert_count === 0 ? 'ok' : 'warn'}`}>
            {report.alert_count === 0
              ? '✅ 要確認の項目はありません'
              : `⚠️ 要確認の項目が ${report.alert_count} 件あります。下の内容を確認してください。`}
          </div>

          <div className="opc-sec-stats">
            <div><span>テナント</span><strong>{s.tenant_count}</strong><small>新規 {s.new_tenants}</small></div>
            <div><span>ユーザー</span><strong>{s.user_count}</strong><small>新規 {s.new_users}・凍結 {s.frozen_count}</small></div>
            <div><span>ログイン</span><strong>{s.login_count}</strong><small>{s.active_users}人</small></div>
            <div><span>AIマニュアル</span><strong>{s.video_manuals}</strong><small>件作成</small></div>
          </div>

          <h3 className="opc-sec-group">不審なログイン</h3>
          <Block
            title="初めてのIPアドレスからのログイン"
            hint="これまでログインしたことのない場所からのログインです。本人の出張先・自宅などであれば問題ありません。心当たりがなさそうな場合は、テナントの管理者に確認してください。"
            rows={report.new_ip_logins}
            columns={['ユーザー', 'IPアドレス', 'ブラウザ', '初回', '回数']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><Who row={x} onOpenTenant={onOpenTenant} /></td>
                <td className="opc-mono">{x.ip_address}</td>
                <td>{x.browser || '—'}</td>
                <td>{formatDateTime(x.first_at)}</td>
                <td>{x.count}</td>
              </tr>
            )}
          />
          <Block
            title="3つ以上のIPアドレスからログインしたユーザー"
            hint="スマホの回線切り替えなどでも増えますが、短期間に多い場合はアカウントの共有や乗っ取りの可能性があります。"
            rows={report.multi_ip_users}
            columns={['ユーザー', 'IPの数', 'ログイン回数', 'IPアドレス（一部）']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><Who row={x} onOpenTenant={onOpenTenant} /></td>
                <td>{x.ip_count}</td>
                <td>{x.login_count}</td>
                <td className="opc-mono">{(x.ips || []).join(', ')}</td>
              </tr>
            )}
          />
          <Block
            title="複数のテナントで使われたIPアドレス"
            hint="別々の会社のアカウントが同じ場所からログインしています。同じ人が複数の会社を管理している場合もありますが、第三者によるアクセスの可能性もあります。"
            rows={report.shared_ips}
            columns={['IPアドレス', 'テナント数', 'ユーザー数', 'テナント（一部）']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td className="opc-mono">{x.ip_address}</td>
                <td>{x.tenant_count}</td>
                <td>{x.user_count}</td>
                <td>{(x.tenants || []).join('、')}</td>
              </tr>
            )}
          />
          <Block
            title="深夜（0〜5時）のログイン"
            level="info"
            hint="業務時間外のログインです。夜勤などがなければ確認してください。"
            rows={report.night_logins}
            columns={['ユーザー', '日時', 'IPアドレス', 'ブラウザ']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><Who row={x} onOpenTenant={onOpenTenant} /></td>
                <td>{formatDateTime(x.logged_in_at)}</td>
                <td className="opc-mono">{x.ip_address || '—'}</td>
                <td>{x.browser || '—'}</td>
              </tr>
            )}
          />

          <h3 className="opc-sec-group">アカウント・権限の変更</h3>
          <Block
            title="ユーザー追加・権限やメールの変更・二段階認証の登録・凍結・リセットなど"
            hint="身に覚えのない二段階認証の登録は、パスワードを盗んだ第三者による登録の可能性があります。"
            level="info"
            rows={report.account_events}
            columns={['日時', 'テナント', '操作した人', '内容']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td>{formatDateTime(x.occurred_at)}</td>
                <td><button className="opc-link" onClick={() => onOpenTenant(x.tenant_id)}>{x.company_name || x.tenant_id}</button></td>
                <td>{x.user_display_name || '—'}</td>
                <td>
                  「{x.target_display_name || '—'}」を{ACTION_LABELS[x.action] || x.action}
                  {x.action === 'mfa_registered' && x.changes?.二段階認証?.after && (
                    <div className="opc-user-sub">{x.changes.二段階認証.after}</div>
                  )}
                  {x.action === 'user_updated' && x.changes && (
                    <div className="opc-user-sub">
                      {Object.entries(x.changes)
                        .filter(([k]) => k === '権限' || k === 'メールアドレス')
                        .map(([k, v]) => `${k}: ${v?.before ?? '—'} → ${v?.after ?? '—'}`)
                        .join(' ／ ')}
                    </div>
                  )}
                </td>
              </tr>
            )}
          />

          <h3 className="opc-sec-group">設定の弱点</h3>
          <Block
            title="MFA未設定の管理者"
            hint="管理者はユーザーの追加や権限の変更ができるため、乗っ取られると影響が大きくなります。MFAの設定をお願いしてください。"
            rows={report.admins_without_mfa}
            columns={['ユーザー', 'メール', '登録日', '最終ログイン']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><Who row={{ ...x, role: 'admin' }} onOpenTenant={onOpenTenant} /></td>
                <td>{x.email || '—'}</td>
                <td>{formatDateTime(x.created_at)}</td>
                <td>{formatDateTime(x.last_login_at)}</td>
              </tr>
            )}
          />
          <Block
            title="90日以上ログインしていないユーザー"
            level="info"
            hint="退職者や使われていないアカウントが残っていると、乗っ取りの入口になります。不要なら凍結をおすすめしてください。（期間の切り替えに関係なく、現時点の状態です）"
            rows={report.dormant_users}
            columns={['ユーザー', '登録日', '最終ログイン']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><Who row={x} onOpenTenant={onOpenTenant} /></td>
                <td>{formatDateTime(x.created_at)}</td>
                <td>{x.last_login_at ? formatDateTime(x.last_login_at) : 'ログインなし'}</td>
              </tr>
            )}
          />

          <h3 className="opc-sec-group">利用状況</h3>
          <Block
            title="ユーザー数が上限の8割以上のテナント"
            level="info"
            hint="プラン変更のご案内のタイミングの目安になります。"
            rows={report.tenants_near_limit}
            columns={['テナント', 'ユーザー数']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td><button className="opc-link" onClick={() => onOpenTenant(x.tenant_id)}>{x.company_name || x.tenant_name || x.tenant_id}</button></td>
                <td>{x.user_count} / {x.max_users} 人</td>
              </tr>
            )}
          />
          <Block
            title="運営者アカウントのログイン"
            level="info"
            hint="運営者アカウントは全テナントを操作できます。心当たりのないログインがあれば、すぐにパスワードを変更してください。"
            rows={report.operator_logins}
            columns={['日時', 'IPアドレス', 'ブラウザ']}
            renderRow={(x, i) => (
              <tr key={i}>
                <td>{formatDateTime(x.logged_in_at)}</td>
                <td className="opc-mono">{x.ip_address || '—'}</td>
                <td>{x.browser || '—'}</td>
              </tr>
            )}
          />
        </>
      )}
    </div>
  );
}

export default OperatorSecurity;
